import { dateQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { textQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SafeDateTimePicker } from "components/SafeDateTimePicker";
import { useNotifyStatus } from "contexts/status";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useCallback, useMemo, useState } from "react";
import {
  RedactFootagePreviewItem,
  useListCameraAnglesQuery,
  useListInstallationLocationsQuery,
  useRedactFootageMutation,
  useRedactFootagePreviewMutation,
} from "src/store/api/generatedApi";
import { RedactionJobProgress } from "./RedactionJobProgress";
import { RedactionPreviewTable } from "./RedactionPreviewTable";
import { RedactionVideoPreview } from "./RedactionVideoPreview";

dayjs.extend(utc);

export const RedactFootageTab = () => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();

  const [locationName, setLocationName] = useNavigationQuery(
    "redactionLocation",
    "",
    textQuery,
    "replace"
  );
  const [angleName, setAngleName] = useNavigationQuery(
    "redactionAngle",
    "",
    textQuery,
    "replace"
  );
  const [startTime, setStartTime] = useNavigationQuery(
    "redactionStart",
    () => dayjs().utc().subtract(1, "day"),
    dateQuery,
    "replace"
  );
  const [endTime, setEndTime] = useNavigationQuery(
    "redactionEnd",
    () => dayjs().utc(),
    dateQuery,
    "replace"
  );

  const [previewItems, setPreviewItems] = useState<
    RedactFootagePreviewItem[] | null
  >(null);
  const [previewSignature, setPreviewSignature] = useState<string | null>(null);
  const [previewTotalSegments, setPreviewTotalSegments] = useState(0);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isFinalConfirmDialogOpen, setIsFinalConfirmDialogOpen] =
    useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const { data: installationLocations } = useListInstallationLocationsQuery({
    tenantName,
  });

  const { data: cameraAngles } = useListCameraAnglesQuery({ tenantName });

  const anglesForLocation = useMemo(() => {
    if (!cameraAngles || !locationName) return [];
    return cameraAngles.filter((angle) => angle.location_name === locationName);
  }, [cameraAngles, locationName]);

  const [fetchPreview, { isLoading: isLoadingPreview }] =
    useRedactFootagePreviewMutation();

  const [submitRedaction, { isLoading: isSubmittingRedaction }] =
    useRedactFootageMutation();

  const requestBody = useMemo(
    () => ({
      location_name: locationName,
      angle_name: angleName,
      start: startTime.utc().toISOString(),
      end: endTime.utc().toISOString(),
    }),
    [locationName, angleName, startTime, endTime]
  );

  const requestSignature = JSON.stringify(requestBody);
  const previewMatches = previewSignature === requestSignature;
  const canPreview =
    locationName !== "" && angleName !== "" && startTime.isBefore(endTime);

  const handlePreview = useCallback(async () => {
    try {
      const previewResponse = await fetchPreview({
        tenantName,
        redactFootageRequest: requestBody,
      }).unwrap();
      setPreviewItems(previewResponse.items);
      setPreviewSignature(requestSignature);
      setPreviewTotalSegments(previewResponse.total_segments);
      setActiveJobId(null);
    } catch {
      notifyStatus({ type: "error", text: "Failed to load preview" });
    }
  }, [fetchPreview, tenantName, requestBody, requestSignature, notifyStatus]);

  const handleConfirmRedaction = useCallback(async () => {
    if (!previewMatches) return;
    try {
      const jobResponse = await submitRedaction({
        tenantName,
        redactFootageRequest: requestBody,
      }).unwrap();
      setActiveJobId(jobResponse.job_id);
      setIsConfirmDialogOpen(false);
      setIsFinalConfirmDialogOpen(false);
      setPreviewItems(null);
      notifyStatus({ type: "success", text: t("redactFootageJobStarted") });
    } catch {
      notifyStatus({ type: "error", text: "Failed to start redaction" });
    }
  }, [
    submitRedaction,
    tenantName,
    requestBody,
    notifyStatus,
    t,
    previewMatches,
  ]);

  const handleLocationChange = useCallback(
    (value: string) => {
      setLocationName(value);
      setAngleName("");
      setPreviewItems(null);
    },
    [setAngleName, setLocationName]
  );

  return (
    <Stack spacing={3} sx={{ p: 2 }}>
      <Typography variant="h6">{t("redactFootageTab")}</Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              fullWidth
              label={t("redactFootageSelectLocation")}
              value={locationName}
              onChange={(e) => {
                handleLocationChange(e.target.value);
              }}
            >
              {(installationLocations ?? []).map((loc) => (
                <MenuItem key={loc.name} value={loc.name}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label={t("redactFootageSelectAngle")}
              value={angleName}
              onChange={(e) => {
                setAngleName(e.target.value);
                setPreviewItems(null);
              }}
              disabled={anglesForLocation.length === 0}
            >
              {anglesForLocation.map((angle) => (
                <MenuItem
                  key={`${angle.location_name}-${angle.name}`}
                  value={angle.name}
                >
                  {angle.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <SafeDateTimePicker
              label={t("start")}
              value={startTime}
              onChange={(v) => {
                if (v) {
                  setStartTime(v.utc());
                  setPreviewItems(null);
                }
              }}
              ampm={false}
            />
            <SafeDateTimePicker
              label={t("end")}
              value={endTime}
              onChange={(v) => {
                if (v) {
                  setEndTime(v.utc());
                  setPreviewItems(null);
                }
              }}
              ampm={false}
            />
          </Stack>

          <Button
            variant="contained"
            onClick={() => void handlePreview()}
            disabled={!canPreview || isLoadingPreview}
          >
            {t("redactFootagePreview")}
          </Button>
        </Stack>
      </Paper>

      {previewMatches && previewItems !== null && previewItems.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <RedactionPreviewTable
              items={previewItems}
              totalSegments={previewTotalSegments}
            />
            <RedactionVideoPreview
              items={previewItems}
              startIso={startTime.utc().toISOString()}
              endIso={endTime.utc().toISOString()}
            />
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setIsConfirmDialogOpen(true);
              }}
              disabled={isSubmittingRedaction}
            >
              {t("redactFootageConfirm")}
            </Button>
          </Stack>
        </Paper>
      )}

      {previewMatches && previewItems !== null && previewItems.length === 0 ? (
        <Alert severity="info">{t("redactFootageNoSegments")}</Alert>
      ) : null}

      {activeJobId !== null && <RedactionJobProgress jobId={activeJobId} />}

      <Dialog
        open={isConfirmDialogOpen && previewMatches}
        onClose={() => {
          setIsConfirmDialogOpen(false);
        }}
      >
        <DialogTitle>{t("redactFootageConfirm")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("redactFootageWarning", { count: previewTotalSegments })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsConfirmDialogOpen(false);
            }}
            disabled={isSubmittingRedaction}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setIsConfirmDialogOpen(false);
              setIsFinalConfirmDialogOpen(true);
            }}
            disabled={isSubmittingRedaction}
          >
            {t("redactFootageConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isFinalConfirmDialogOpen && previewMatches}
        onClose={() => {
          setIsFinalConfirmDialogOpen(false);
        }}
      >
        <DialogTitle>{t("redactFootageConfirm")}</DialogTitle>
        <DialogContent>
          <Typography>{t("redactFootageWarningFinal")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsFinalConfirmDialogOpen(false);
            }}
            disabled={isSubmittingRedaction}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmRedaction()}
            disabled={isSubmittingRedaction}
          >
            {t("redactFootageConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
